import React, { useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * 이벤트 이력 '처리' 열 — 깔끔한 상태 배지 하나만 보여주고,
 * 상세(확인자·조치내용·확인시각)는 마우스 오버 / 클릭 시 팝오버로 표시.
 * 팝오버는 표의 overflow에 잘리지 않도록 body 포털 + fixed 로 띄운다.
 */
export default function AckCell({ ack }) {
  const [pos, setPos] = useState(null); // { top, right } | null
  const btnRef = useRef(null);
  const timer = useRef(null);

  const show = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({ top: r.bottom + 8, right: Math.max(8, window.innerWidth - r.right) });
  }, []);

  const hide = useCallback(() => {
    timer.current = setTimeout(() => setPos(null), 120);
  }, []);

  if (!ack) return <span className="text-gray-300 dark:text-gray-600">—</span>;

  // 오탐 표시가 확인완료보다 우선(오탐이면 오탐으로 보여준다).
  const isFp = ack.fp;

  if (!ack.done && !isFp) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-500 dark:bg-white/5 dark:text-gray-400">
        미확인
      </span>
    );
  }

  const badge = isFp
    ? {
        wrap: "bg-rose-50 text-rose-700 ring-rose-200 hover:bg-rose-100 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/25",
        dot: "bg-rose-500",
        icon: "!",
        label: "오탐",
        headText: "오탐(오인식)",
        who: ack.fpBy || ack.by || "-",
        whoLabel: "처리자",
        whenLabel: "오탐 처리 시각",
        when: ack.fpAt || ack.at || "-",
      }
    : {
        wrap: "bg-emerald-50 text-emerald-700 ring-emerald-200 hover:bg-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/25",
        dot: "bg-emerald-500",
        icon: "✓",
        label: "확인완료",
        headText: "확인 완료",
        who: ack.by || "-",
        whoLabel: "확인자",
        whenLabel: "확인 시각",
        when: ack.at || "-",
      };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onMouseEnter={show}
        onMouseLeave={hide}
        onClick={() => (pos ? hide() : show())}
        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold ring-1 transition ${badge.wrap}`}
      >
        <span
          className={`flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-bold leading-none text-white ${badge.dot}`}
        >
          {badge.icon}
        </span>
        {badge.label}
      </button>

      {pos &&
        createPortal(
          <div
            style={{
              position: "fixed",
              top: pos.top,
              right: pos.right,
              zIndex: 100000,
            }}
            className="w-64 rounded-xl border border-gray-200 bg-white p-4 text-left shadow-2xl ring-1 ring-black/5 dark:border-white/10 dark:bg-[#0F172A] dark:ring-white/10"
            onMouseEnter={show}
            onMouseLeave={hide}
          >
            <div className="mb-3 flex items-center gap-1.5 border-b border-gray-100 pb-2.5 dark:border-white/10">
              <span
                className={`flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-bold leading-none text-white ${badge.dot}`}
              >
                {badge.icon}
              </span>
              <span
                className={`text-xs font-bold ${
                  isFp
                    ? "text-rose-700 dark:text-rose-300"
                    : "text-emerald-700 dark:text-emerald-300"
                }`}
              >
                {badge.headText}
              </span>
            </div>
            <div className="space-y-2.5">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                  {badge.whoLabel}
                </div>
                <div className="mt-0.5 break-words text-sm font-semibold text-gray-900 dark:text-white">
                  {badge.who}
                </div>
              </div>
              {!isFp && (
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                    조치 내용
                  </div>
                  <div className="mt-0.5 break-words text-sm text-gray-700 dark:text-gray-200">
                    {ack.note ? `“${ack.note}”` : "(내용 없음)"}
                  </div>
                </div>
              )}
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                  {badge.whenLabel}
                </div>
                <div className="mt-0.5 text-sm text-gray-600 dark:text-gray-300">
                  {badge.when}
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
